import { useEffect } from "react";
import Footer from "../components/atoms/footer";


const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" }); // Scrolls to the top when the component mounts
  }, []);
  return (
    <div>
      <div className="container mt-4">
        <h1>Privacy Policy</h1>
        <p>
          <strong>Last updated:</strong> May 29, 2026
        </p>

        <p>
          This Privacy Notice for Enlive Systems, Inc.{" "}
          <strong>("we," "us," "our", "Enlive", or "the Company")</strong>, describes how and why
          we might access, collect, store, use, and/or share{" "}
          <strong>("process")</strong> your personal information when you use
          our services <strong>("Services")</strong>, including when you:
        </p>

        <ul>
          <li>
            Visit the Enlive website, or any website of ours that links to this Privacy
            Notice.
          </li>
          <li>
            Use Enlive, a platform for users to interact with various AI tools and receive personal 
            feedback with their data.
          </li>
          <li>
            Engage with us in other related ways, including any sales,
            marketing, or events.
          </li>
        </ul>

        <h2>Table of Contents</h2>
        <ol>
          <li>Collection of Personal Information</li>
          <li>Protection of Personal Information</li>
          <li>Security</li>
          <li>Our Use of Personal Information</li>
          <li>Accessing and Controlling Your Personal Information</li>
          <li>Cookies</li>
          <li>Age Restriction</li>
          <li>Privacy Policy Changes and Notifications</li>
          <li>Contact Us</li>
        </ol>

        <h2>1. Collection of Personal Information</h2>
        <h4>Non-Personally Identifiable Information</h4>
        <p>
          We automatically collect certain information when you visit, use, or
          navigate the Services. This information does not reveal your specific
          identity (like your name or contact information) but may include
          device and usage information, such as your Internet Protocol (IP)
          address, browser and device characteristics, operating system,
          language preferences, referring URLs, device name, country, location,
          information about how and when you use our Services, and other
          technical information. This information is primarily needed to
          maintain the security and operation of our Services, and for our
          internal analytics and reporting purposes. Enlive only discloses logged
          in user IP addresses under the same circumstances that it uses and discloses
          personally identifying information as described below.
        </p>

        <h4>Personally Identifiable Information</h4>
        <p>
          We collect personal information that you voluntarily provide to us
          when you register on the Services, express an interest in obtaining
          information about us or our products and Services, when you
          participate in activities on the Services, or otherwise when you
          contact us. When providing personal data such as external data and
          file contents is optional, and you choose not to share this personal
          data, features like personalization that use such data will not work
          for you.
        </p>
        <p>
          To provide certain features of the Services, with your consent or as
          otherwise permitted by applicable law, we process the following
          categories of sensitive information:
        </p>

        <div>
          <ul>
            <li>
              <strong>Social Login Data:</strong> we provide you with the
              option to register with us using your existing social
              account details, like your Google or Microsoft account. If you
              choose to register in this way, we will collect certain profile
              information about you from the registered social account, 
              such as your email or username.
            </li>
            <li>
              <strong>Your External Data:</strong> you have the option to add
              certain external data to your Enlive profile, which
              will allow us to read and copy contents of your 
              Google Drive, Microsoft OneDrive, Gmail, Outlook applications,
              and computer Desktop files that you give us consent to process. 
              This data is stored independently from other users and is used
              exclusively to improve your AI interactions on Enlive. See further
              details on our uses of your external data below in Section 4. 
            </li>
            <li>
              Our use of information received from Google APIs adheres to the {" "}
               <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "blue" }}
              >
                Google API Services User Data Policy
              </a>
            </li>
            <li>
              Our use of information received from Microsoft Graph APIs adheres 
              to the {" "} 
               <a
                href="https://www.microsoft.com/en-us/privacy/privacystatement"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "blue" }}
              >
                Microsoft Privacy Statement
              </a>
            </li>
          </ul>
        </div>

        <h2>2. Protection of Personal Information</h2>
        <p>
          We do not rent of sell your personally identifiable information to
          anyone. Thus, we do not offer an opt-out to the sale of personal data.
          Our employees, contractors, or affiliated organizations may see your
          personally identifiable information only if (i) they need to know the
          information to provide the Services and (ii) they are under a binding
          legal agreement not to disclose it to others. We may disclose your
          personal information in response to a subpoena, court order, or other
          governmental request, or when in good faith believe disclosure is
          necessary to protect the rights and property of Enlive Systems,
          Inc. We take all measures reasonably necessary to protect against the
          unauthorized access, use, alteration, or destruction of potentially
          personally identifiable information or personally identifiable
          information.
        </p>

        <h2>3. Security</h2>
        <p>
          We have implemented appropriate and reasonable technical and
          organizational security measures designed to protect the security of
          any personal information of yours that we process. However, despite our safeguards
          and efforts to secure your information, no electronic transmission
          over the Internet or information storage technology can be guaranteed
          to be 100% secure, so we cannot promise or guarantee that hackers,
          cybercriminals, or other unauthorized third parties will not be able
          to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your
          personal information, transmission of personal information to and from
          our Services is at your own risk. You should only access Enlive
          within a secure environment.
        </p>

        <h2>4. Our Use of Personal Information</h2>
        <p>We process personal information from (i) your Social Login data and (ii) your external data for several reasons:</p>
        <ul>
          <li>
            To facilitate account creation, authentication, and otherwise manage
            user accounts.
          </li>
          <li>
            To enhance the information you receive during your interactions with external
            AI models or tools on Enlive.
          </li>
          <li>
            To complete any task or provide any service you have
            requested in AI conversations on Enlive.
          </li>
          <li>
            When we believe it is necessary to identify usage trends and to
            evaluate and improve our Services, products, marketing, and your
            experience.
          </li>
        </ul>
        <p>
          We will ask for your consent before using your information for a
          purpose that is not covered in this Privacy Policy.
        </p>

        <h2>5. Accessing and Controlling Your Personal Information</h2>
        <p>
          We rely on your consent to process your external personal information
          such as the contents of your Google Drive, Gmail, Outlook, and
          Microsoft OneDrive, and Desktop. You have the right to withdraw your consent at any
          time. You can withdraw your consent by navigating to the 
          My Profile page, either by (i) clicking on the 'My Profile' option in
          the Navigation Bar in the top left of the home page, or by (ii) clicking
          on the Profile Percentage Indicator in the top right of the home page.
          Once on the My Profile page, you can toggle 'off' to withdraw consent 
          from any of your activated data connections. Toggling a connection
          'off' will delete the associated data from your Enlive profile.
        </p>
        <p>
          If you would at any time like to review or change the information in
          your account or terminate your account, you can log in to your account
          and navigate to the My Profile page using either method above. 
          Upon your request to terminate
          your account, we will deactivate or delete your account and
          information from our active databases. However, we may retain 
          some information in our files to prevent fraud, troubleshoot problems, 
          assist with any investigations, enforce our legal terms and/or comply 
          with applicable legal requirements.
        </p>

        <h2>6. Cookies</h2>
        <p>
          A cookie is a string of information that a website stores on a
          visitor's computer, and that the visitor's browser provides to the
          website each time the visitor returns. We may use cookies and similar
          tracking technologies (like web beacons and pixels) to gather
          information when you interact with our Services. Some online tracking
          technologies help us maintain the security of our Services and your
          account, prevent crashes, fix bugs, save your preferences, and assist
          with basic site functions.
        </p>
        <p>
          We also permit third parties and service providers to use online
          tracking technologies on our Services for analytics and advertising,
          including to help manage and display advertisements, to tailor
          advertisements to your interests, which may appear either on our 
          Services or on other websites.
        </p>
        <p>
          If you do not wish to have cookies placed on your computer, you
          should set your browser to refuse cookies before using our Services.
          The Enlive website may not function properly without the aid of cookies. By continuing
          to use our Services without changing your cookie settings, you hereby
          acknowledge and agree to our use of cookies.
        </p>

        <h2> 7. Age Restriction</h2>
        <p>
          By using Enlive, you represent that you are at
          least 13 or that you are the parent or guardian of such a minor and
          consent to such minor dependent’s use of the Services. We do not knowingly 
          collect, solicit data from, or market to children under 13 years of 
          age, nor do we knowingly sell such personal information. If we learn
          that personal information from users less than 13 years of age has
          been collected, we will deactivate the account and take reasonable
          measures to promptly delete such data from our records.
        </p>

        <h2>8. Privacy Policy Changes and Notifications</h2>
        <p>
          Enlive may change its Privacy Policy from time to time, at our sole
          discretion. We will display updates in the Privacy Policy tab and mark
          the date of the update. You are responsible for checking the Privacy
          Policy for updates and Enlive has no obligation to notify you of such
          updates. Your continued use of this site after any change in this
          Privacy Policy will constitute your acceptance of such change.
        </p>

        <h2>9. Contact Us</h2>
        <p>
          If you have questions or comments, please email us at ask.support@enlive.inc.
        </p>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
